import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, X, Upload, Image, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Wedding", "Corporate", "Birthday", "Private", "Special Events"];

const AdminGalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Wedding",
    file: null,
    url: ""
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery images");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      if (editingImage) {
        // Update existing image
        await axios.put(`${API}/admin/gallery/${editingImage.id}`, {
          title: formData.title,
          description: formData.description,
          category: formData.category
        });
        toast.success("Image updated successfully");
      } else {
        // Create new image
        const submitData = new FormData();
        submitData.append("title", formData.title);
        submitData.append("description", formData.description);
        submitData.append("category", formData.category);
        
        if (formData.file) {
          submitData.append("file", formData.file);
        } else if (formData.url) {
          submitData.append("url", formData.url);
        } else {
          toast.error("Please upload an image or provide a URL");
          setUploading(false);
          return;
        }

        await axios.post(`${API}/admin/gallery`, submitData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success("Image added successfully");
      }

      setShowModal(false);
      resetForm();
      fetchImages();
    } catch (error) {
      console.error("Error saving image:", error);
      toast.error(error.response?.data?.detail || "Failed to save image");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`);
      toast.success("Image deleted successfully");
      fetchImages();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      description: image.description || "",
      category: image.category,
      file: null,
      url: image.url
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "Wedding",
      file: null,
      url: ""
    });
    setEditingImage(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 10MB");
        return;
      }
      setFormData({ ...formData, file, url: "" });
    }
  };

  const getImageUrl = (url) => {
    if (url.startsWith("/uploads")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const filteredImages = selectedCategory === "All" 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  const seedGallery = async () => {
    try {
      await axios.post(`${API}/admin/gallery/seed`);
      toast.success("Gallery seeded with default images");
      fetchImages();
    } catch (error) {
      console.error("Error seeding gallery:", error);
      toast.error("Failed to seed gallery");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gallery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            Gallery Management
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Upload and manage gallery images by category
          </p>
        </div>
        <div className="flex gap-3">
          {images.length === 0 && (
            <button
              onClick={seedGallery}
              className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37]/10 transition-colors"
              data-testid="seed-gallery-btn"
            >
              Load Default Images
            </button>
          )}
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded hover:bg-[#C4A030] transition-colors"
            data-testid="add-image-btn"
          >
            <Plus className="w-4 h-4" />
            Add Image
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory("All")}
          className={`px-4 py-2 rounded text-sm transition-colors ${
            selectedCategory === "All"
              ? "bg-[#D4AF37] text-[#050505]"
              : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-[#D4AF37]"
          }`}
        >
          All ({images.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = images.filter(img => img.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded text-sm transition-colors ${
                selectedCategory === cat
                  ? "bg-[#D4AF37] text-[#050505]"
                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-[#D4AF37]"
              }`}
            >
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Gallery Grid */}
      {filteredImages.length === 0 ? (
        <div className="text-center py-12 bg-[#0F0F0F] rounded-lg border border-[rgba(212,175,55,0.15)]">
          <Image className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">No images found</p>
          <p className="text-[#a1a1aa] text-sm mt-1">Add images to showcase your events</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              className="group relative bg-[#0F0F0F] rounded-lg overflow-hidden border border-[rgba(212,175,55,0.15)]"
              data-testid={`gallery-image-${image.id}`}
            >
              <div className="aspect-square relative">
                <img
                  src={getImageUrl(image.url)}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target) target.src = "https://via.placeholder.com/400?text=Image+Not+Found";
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    onClick={() => handleEdit(image)}
                    className="p-2 bg-[#D4AF37] rounded-full text-[#050505] hover:bg-[#C4A030]"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <a
                    href={getImageUrl(image.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-[#1A1A1A] rounded-full text-[#A1A1AA] hover:text-[#D4AF37]"
                    title="View Full Size"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-[#F5F5F0] font-medium truncate">{image.title}</h3>
                <span className="inline-block mt-1 px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded">
                  {image.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F0F0F] rounded-lg max-w-lg w-full border border-[rgba(212,175,55,0.15)]">
            <div className="flex justify-between items-center p-4 border-b border-[rgba(212,175,55,0.15)]">
              <h2 className="text-lg font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                {editingImage ? "Edit Image" : "Add New Image"}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="text-[#A1A1AA] hover:text-[#F5F5F0]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0] font-medium placeholder-[#71717a] focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Enter image title"
                  required
                  data-testid="image-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0] font-medium placeholder-[#71717a] focus:border-[#D4AF37] focus:outline-none resize-none"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0] font-medium placeholder-[#71717a] focus:border-[#D4AF37] focus:outline-none"
                  data-testid="image-category-select"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Image Upload / URL */}
              {!editingImage && (
                <>
                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">Upload Image</label>
                    <div className="border-2 border-dashed border-[rgba(212,175,55,0.3)] rounded-lg p-6 text-center hover:border-[#D4AF37] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="image-upload"
                        data-testid="image-file-input"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                        {formData.file ? (
                          <p className="text-[#D4AF37]">{formData.file.name}</p>
                        ) : (
                          <p className="text-[#A1A1AA]">Click to upload (max 10MB)</p>
                        )}
                      </label>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[rgba(212,175,55,0.15)]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-[#0F0F0F] text-[#a1a1aa]">or</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">Image URL</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value, file: null })}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded text-[#F5F5F0] font-medium placeholder-[#71717a] focus:border-[#D4AF37] focus:outline-none"
                      placeholder="https://example.com/image.jpg"
                      data-testid="image-url-input"
                    />
                  </div>
                </>
              )}

              {/* Preview for editing */}
              {editingImage && (
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Current Image</label>
                  <img
                    src={getImageUrl(formData.url)}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded"
                  />
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded hover:bg-[#C4A030] transition-colors disabled:opacity-50"
                  data-testid="save-image-btn"
                >
                  {uploading ? "Saving..." : editingImage ? "Update" : "Add Image"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGalleryPage;
